import React, { useState, useEffect, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { cancellationPolicyURL } from "../../../../utils/urls";

const emptyRule = () => ({ daysBeforeCheckIn: 0, refundPercentage: 0, description: "" });

const emptyForm = () => ({
  _id: null,
  name: "",
  description: "",
  isNonRefundable: false,
  refundRules: [emptyRule()]
});

const CancellationPoliciesTab = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [isEditing, setIsEditing] = useState(false);

  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${cancellationPolicyURL}/${facilityId}`, "GET");
      if (response.success) {
        const list = response.data?.data || response.data || [];
        setPolicies(Array.isArray(list) ? list : []);
      } else {
        toastify(response.error || "Failed to load cancellation policies", "error");
      }
    } catch (error) {
      console.error("Error fetching cancellation policies:", error);
      toastify("Failed to load cancellation policies", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const openAddDialog = () => {
    setForm(emptyForm());
    setIsEditing(false);
    setShowDialog(true);
  };

  const openEditDialog = (policy) => {
    setForm({
      _id: policy._id,
      name: policy.name || "",
      description: policy.description || "",
      isNonRefundable: Boolean(policy.isNonRefundable),
      refundRules: policy.refundRules && policy.refundRules.length > 0
        ? policy.refundRules.map((r) => ({ ...r }))
        : [emptyRule()]
    });
    setIsEditing(true);
    setShowDialog(true);
  };

  const handleRuleChange = (index, field, value) => {
    const updatedRules = [...form.refundRules];
    updatedRules[index] = { ...updatedRules[index], [field]: value };
    setForm({ ...form, refundRules: updatedRules });
  };

  const addRule = () => {
    setForm({ ...form, refundRules: [...form.refundRules, emptyRule()] });
  };

  const removeRule = (index) => {
    setForm({ ...form, refundRules: form.refundRules.filter((_, i) => i !== index) });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toastify("Policy name is required", "error");
      return;
    }

    if (!form.isNonRefundable && form.refundRules.length === 0) {
      toastify("Add at least one refund rule, or mark the policy as non-refundable", "error");
      return;
    }

    try {
      setSaving(true);
      const facilityId = await getItem("selectedFacilityId");

      const payload = {
        name: form.name.trim(),
        description: form.description,
        isNonRefundable: form.isNonRefundable,
        refundRules: form.isNonRefundable ? [] : form.refundRules
      };

      const response = isEditing
        ? await makeRequest2(`${cancellationPolicyURL}/${facilityId}/${form._id}`, "PUT", payload)
        : await makeRequest2(`${cancellationPolicyURL}/${facilityId}`, "POST", payload);

      if (response.success) {
        toastify(`Cancellation policy ${isEditing ? "updated" : "created"} successfully`, "success");
        setShowDialog(false);
        fetchPolicies();
      } else {
        toastify(response.error || "Failed to save cancellation policy", "error");
      }
    } catch (error) {
      console.error("Error saving cancellation policy:", error);
      toastify("Failed to save cancellation policy", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (policy) => {
    confirmDialog({
      message: `Delete cancellation policy "${policy.name}"? This cannot be undone.`,
      header: "Confirm Delete",
      icon: "ti ti-alert-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const facilityId = await getItem("selectedFacilityId");
          const response = await makeRequest2(`${cancellationPolicyURL}/${facilityId}/${policy._id}`, "DELETE");
          if (response.success) {
            toastify("Cancellation policy deleted", "success");
            fetchPolicies();
          } else {
            toastify(response.error || "Failed to delete cancellation policy", "error");
          }
        } catch (error) {
          console.error("Error deleting cancellation policy:", error);
          toastify("Failed to delete cancellation policy", "error");
        }
      }
    });
  };

  const rulesBodyTemplate = (rowData) => {
    if (rowData.isNonRefundable) {
      return <span className="badge bg-danger">Non-refundable</span>;
    }
    if (!rowData.refundRules || rowData.refundRules.length === 0) {
      return <span className="text-muted">No rules</span>;
    }
    return (
      <div>
        {rowData.refundRules.map((rule, i) => (
          <div key={i} className="small">
            {rule.refundPercentage}% refund if cancelled {rule.daysBeforeCheckIn}+ days before check-in
          </div>
        ))}
      </div>
    );
  };

  const actionsBodyTemplate = (rowData) => (
    <div className="d-flex gap-2">
      <Button
        icon="ti ti-edit"
        className="p-button-rounded p-button-info p-button-sm"
        onClick={() => openEditDialog(rowData)}
        tooltip="Edit"
        tooltipOptions={{ position: "top" }}
      />
      <Button
        icon="ti ti-trash"
        className="p-button-rounded p-button-danger p-button-sm"
        onClick={() => handleDelete(rowData)}
        tooltip="Delete"
        tooltipOptions={{ position: "top" }}
      />
    </div>
  );

  return (
    <div className="card">
      <ConfirmDialog />
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Cancellation Policies</h5>
        <Button label="Add Policy" icon="ti ti-plus" className="p-button-primary" onClick={openAddDialog} />
      </div>
      <div className="card-body">
        <p className="text-muted small mb-3">
          Create named cancellation policies here, then assign one to each unit when listing it
          for booking. A single policy can be reused across many units.
        </p>
        <DataTable
          value={policies}
          loading={loading}
          emptyMessage="No cancellation policies configured yet."
          paginator
          rows={10}
          className="p-datatable-sm"
          stripedRows
        >
          <Column field="name" header="Name" sortable />
          <Column field="description" header="Description" />
          <Column header="Refund Rules" body={rulesBodyTemplate} />
          <Column header="Actions" body={actionsBodyTemplate} style={{ width: "120px", textAlign: "center" }} />
        </DataTable>
      </div>

      <Dialog
        header={isEditing ? "Edit Cancellation Policy" : "Add Cancellation Policy"}
        visible={showDialog}
        style={{ width: "700px", maxWidth: "95vw" }}
        onHide={() => setShowDialog(false)}
        footer={
          <div className="d-flex justify-content-end gap-2">
            <Button label="Cancel" className="p-button-text" onClick={() => setShowDialog(false)} />
            <Button label={saving ? "Saving..." : "Save"} icon="ti ti-check" onClick={handleSave} loading={saving} />
          </div>
        }
      >
        <div className="mb-3">
          <label className="form-label fw-bold">Policy Name *</label>
          <InputText
            className="w-100"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Flexible, Moderate, Strict"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <InputTextarea
            className="w-100"
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Shown to guests as the T&C for this policy"
          />
        </div>

        <div className="mb-3 d-flex align-items-center gap-2">
          <Checkbox
            inputId="isNonRefundable"
            checked={form.isNonRefundable}
            onChange={(e) => setForm({ ...form, isNonRefundable: e.checked })}
          />
          <label htmlFor="isNonRefundable" className="mb-0">Non-refundable (no refund under any circumstance)</label>
        </div>

        {!form.isNonRefundable && (
          <div className="mb-2">
            <label className="form-label fw-bold">Refund Rules</label>
            {form.refundRules.map((rule, index) => (
              <div className="row align-items-center mb-2" key={index}>
                <div className="col-4">
                  <InputNumber
                    className="w-100"
                    value={rule.daysBeforeCheckIn}
                    onValueChange={(e) => handleRuleChange(index, "daysBeforeCheckIn", e.value)}
                    min={0}
                    suffix=" days before"
                    placeholder="Days before check-in"
                  />
                </div>
                <div className="col-3">
                  <InputNumber
                    className="w-100"
                    value={rule.refundPercentage}
                    onValueChange={(e) => handleRuleChange(index, "refundPercentage", e.value)}
                    min={0}
                    max={100}
                    suffix="%"
                    placeholder="Refund %"
                  />
                </div>
                <div className="col-4">
                  <InputText
                    className="w-100"
                    value={rule.description}
                    onChange={(e) => handleRuleChange(index, "description", e.target.value)}
                    placeholder="Note (optional)"
                  />
                </div>
                <div className="col-1">
                  <Button
                    icon="ti ti-x"
                    className="p-button-rounded p-button-text p-button-danger p-button-sm"
                    onClick={() => removeRule(index)}
                    disabled={form.refundRules.length === 1}
                  />
                </div>
              </div>
            ))}
            <Button label="Add Rule" icon="ti ti-plus" className="p-button-text p-button-sm" onClick={addRule} />
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default CancellationPoliciesTab;
